import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, User, MailCheck, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { username });
      setSent(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted" data-testid="admin-forgot-password-page">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary mb-4">
              {sent ? <MailCheck size={32} className="text-white" /> : <KeyRound size={32} className="text-white" />}
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">{sent ? 'Check Your Email' : 'Forgot Password'}</h1>
            <p className="text-muted-foreground">
              {sent
                ? 'If that account exists, a reset link has been sent to the registered admin email. The link is valid for 30 minutes.'
                : 'Enter your admin username and we will email a reset link to the registered admin address.'}
            </p>
          </div>

          {sent ? (
            <div data-testid="forgot-password-success" className="text-center space-y-4">
              <button
                type="button"
                data-testid="forgot-password-resend"
                onClick={() => setSent(false)}
                className="text-sm text-muted-foreground hover:text-primary underline underline-offset-4"
              >
                Didn&apos;t receive it? Send again
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form" className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-primary mb-2">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-muted-foreground" size={20} />
                  <input
                    type="text"
                    id="username"
                    data-testid="forgot-password-username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="forgot-password-submit"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/admin/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft size={16} /> Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
